import React from "react";
/**
 * Accordion
 *
 * Lets you create accordions on a page by prefixing h2 headings with a
 * "greater than" character (">"). Everything between this heading and the
 * next one will be considered part of a section.
 *
 * @example
 * <h2>&gt;Section A</h2>
 * <p>Content of section A</p>
 * <p>Content of section A</p>
 *
 * <h2>&gt;Section B</h2>
 * <p>Content of section B</p>
 * <p>Content of section B</p>
 *
 * On initialization, this will display the first section in full but hide the
 * content of the second section, making the section title the toggle.
 *
 * @example
 * Section A
 * Content of section A
 * Content of section A
 * Section B
 */
export function useAccordion<A extends HTMLElement>() {
  const ref = React.useRef<A>(null);

  React.useEffect(() => {
    if (ref.current) {
      let sectionDiv: HTMLDivElement | null = null;
      let sectionId = -1;

      Array.from(ref.current.childNodes).forEach((el) => {
        let nextSection: string;

        if (isH2(el) && el.innerText.match(/^>/)) {
          nextSection = `${++sectionId}`;
          el.innerText = el.innerText.substr(1);
          el.classList.add("accordion__toggle");
          el.classList.add("accordion__toggle--collapsed");
          el.setAttribute("data-accordion-toggle", nextSection);
          el.addEventListener("click", () => {
            toggleSection(nextSection);
          });

          sectionDiv = document.createElement("div");
          sectionDiv.style.display = "none";
          sectionDiv.classList.add("accordion__section");
          sectionDiv.setAttribute("data-accordion-section", nextSection);
          insertAfter(sectionDiv, el);
        } else if (sectionDiv != null) {
          sectionDiv.appendChild(el);
        }
      });

      if (sectionId >= 0) {
        ref.current.classList.add("accordion");
      }

      toggleSection("0");
    }
  }, []);

  return ref;
}

/**
 * Helper to toggle the contents of a section, hiding the content of all
 * other sections
 */
function toggleSection(sId: string) {
  const toggle = document.querySelector(`[data-accordion-toggle="${sId}"]`);
  const section = document.querySelector(`[data-accordion-section="${sId}"]`) as HTMLDivElement | null;

  if (toggle != null && section != null) {
    toggle.classList.toggle("accordion__toggle--collapsed");
    section.style.display = section.style.display === "none" ? "block" : "none";
  }
}

function isH2(el: ChildNode): el is HTMLElement {
  return el.nodeName === "H2";
}

function insertAfter(newNode: HTMLElement, referenceNode: HTMLElement) {
  if (referenceNode.parentNode) {
    referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
  }
}
